import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";

interface Props { workspaceId: string; }

interface DocRow {
  id: string;
  user_id: string;
  doc_type: string;
  title: string;
  form_data: Record<string, string>;
  status: "pending" | "approved" | "rejected";
  ai_comment: string | null;
  admin_comment: string | null;
  capital_change: number;
  submitted_at: string;
  reviewed_at: string | null;
  display_name?: string;
  email?: string;
}

const STATUS_CFG = {
  pending:  { label: "검토 대기", color: "#FF9F43", icon: "ri-time-line",            bg: "rgba(255,159,67,0.08)",  border: "rgba(255,159,67,0.25)" },
  approved: { label: "승인 완료", color: "#00C896", icon: "ri-checkbox-circle-fill",  bg: "rgba(0,200,150,0.08)", border: "rgba(0,200,150,0.25)" },
  rejected: { label: "반려",      color: "#E8375F", icon: "ri-close-circle-fill",     bg: "rgba(232,55,95,0.08)", border: "rgba(232,55,95,0.25)" },
};

const DOC_LABELS: Record<string, string> = {
  contract: "전속 계약서", budget: "예산 기안서", release: "발매 기안서",
  legal: "법적 대응 기안서", approval: "결재 문서",
};

type FilterStatus = "all" | "pending" | "approved" | "rejected";

export default function ApprovalManagerPanel({ workspaceId }: Props) {
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<DocRow | null>(null);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("pending");

  const fetch = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    const { data } = await supabase
      .from("approval_docs")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("submitted_at", { ascending: false });

    if (!data || !data.length) { setDocs([]); setLoading(false); return; }
    const uids = [...new Set(data.map((d: DocRow) => d.user_id))];
    const { data: profiles } = await supabase.from("profiles").select("id,display_name,email").in("id", uids);
    const pmap: Record<string, { display_name: string; email: string }> = {};
    (profiles ?? []).forEach((p: { id: string; display_name: string; email: string }) => { pmap[p.id] = p; });
    setDocs(data.map((d: DocRow) => ({ ...d, display_name: pmap[d.user_id]?.display_name ?? "익명", email: pmap[d.user_id]?.email ?? "-" })));
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAction = async (action: "approved" | "rejected") => {
    if (!selected) return;
    setSaving(true);
    await supabase.from("approval_docs").update({
      status: action,
      admin_comment: comment.trim() || null,
      reviewed_at: new Date().toISOString(),
    }).eq("id", selected.id);
    setSaving(false);
    setSelected(null);
    setComment("");
    await fetch();
  };

  const filtered = docs.filter(d => filterStatus === "all" || d.status === filterStatus);
  const pendingCnt = docs.filter(d => d.status === "pending").length;
  const approvedCnt = docs.filter(d => d.status === "approved").length;
  const rejectedCnt = docs.filter(d => d.status === "rejected").length;

  return (
    <div className="flex gap-5" style={{ minHeight: 600 }}>
      {/* 좌측 목록 */}
      <div className="flex flex-col shrink-0" style={{ width: 380 }}>
        {/* 통계 */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {([["all", "전체", "#888888", docs.length], ["pending", "대기", "#FF9F43", pendingCnt], ["approved", "승인", "#00C896", approvedCnt], ["rejected", "반려", "#E8375F", rejectedCnt]] as [FilterStatus, string, string, number][]).slice(0).map(([s, label, color, cnt]) => (
            <button key={s} type="button" onClick={() => setFilterStatus(s)}
              className={`rounded-xl border px-3 py-2.5 text-center transition-all cursor-pointer col-span-${s === "all" ? "0" : "1"}`}
              style={{ borderColor: filterStatus === s ? `${color}50` : "rgba(255,255,255,0.05)", backgroundColor: filterStatus === s ? `${color}12` : "#111112" }}
            >
              <div className="text-xl font-bold tabular-nums" style={{ color }}>{cnt}</div>
              <div className="text-[#555555] text-[10px]">{label}</div>
            </button>
          )).filter((_, i) => i > 0)}
        </div>

        {/* 필터 탭 */}
        <div className="flex gap-1 mb-3">
          {(["all", "pending", "approved", "rejected"] as FilterStatus[]).map(s => {
            const cfg = s === "all" ? { color: "#888", label: "전체" } : STATUS_CFG[s];
            return (
              <button key={s} type="button" onClick={() => setFilterStatus(s)}
                className={`flex-1 py-1.5 rounded-lg text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap border ${filterStatus === s ? "text-white" : "text-[#444444] border-transparent"}`}
                style={filterStatus === s ? { borderColor: `${cfg.color}40`, backgroundColor: `${cfg.color}18`, color: cfg.color } : {}}
              >
                {s === "all" ? "전체" : STATUS_CFG[s as keyof typeof STATUS_CFG].label}
              </button>
            );
          })}
        </div>

        {/* 목록 */}
        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? [...Array(4)].map((_, i) => <div key={i} className="h-20 bg-white/[0.02] rounded-xl animate-pulse border border-white/[0.04]" />) :
          filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <i className="ri-file-list-3-line text-3xl text-[#222222] block mb-3"></i>
              <p className="text-[#3D3D3D] text-xs">결재 문서가 없어요</p>
            </div>
          ) : filtered.map(doc => {
            const cfg = STATUS_CFG[doc.status];
            const sel = selected?.id === doc.id;
            return (
              <button key={doc.id} type="button" onClick={() => { setSelected(doc); setComment(doc.admin_comment ?? ""); }}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-all cursor-pointer ${sel ? "border-[#FF9F43]/30 bg-[#FF9F43]/[0.04]" : "border-white/[0.05] bg-[#111112] hover:border-white/[0.1]"}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold" style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}>
                      {(doc.display_name ?? "?")[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[#CCCCCC] text-xs font-medium truncate">{doc.display_name}</p>
                      <p className="text-[#3D3D3D] text-[9px] truncate">{doc.email}</p>
                    </div>
                  </div>
                  <span className="flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full border shrink-0" style={{ color: cfg.color, borderColor: `${cfg.color}30`, backgroundColor: cfg.bg }}>
                    <i className={`${cfg.icon} text-xs`}></i>{cfg.label}
                  </span>
                </div>
                <p className="text-[#888888] text-xs font-medium truncate">{doc.title}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[#3D3D3D] text-[9px]">{DOC_LABELS[doc.doc_type] ?? doc.doc_type}</span>
                  <span className="text-[#3D3D3D] text-[9px] tabular-nums">{new Date(doc.submitted_at).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" })}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 우측 상세 */}
      <div className="flex-1 bg-[#111112] rounded-xl border border-white/[0.05] flex flex-col overflow-hidden">
        {!selected ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/[0.03] border border-white/[0.05]">
              <i className="ri-file-list-3-line text-2xl text-[#333333]"></i>
            </div>
            <p className="text-[#555555] text-sm">결재 문서를 선택하세요</p>
            {pendingCnt > 0 && <p className="text-[#FF9F43]/60 text-xs">검토 대기 {pendingCnt}건</p>}
          </div>
        ) : (
          <>
            {/* 상세 헤더 */}
            <div className="px-5 py-4 border-b border-white/[0.05] flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ backgroundColor: `${STATUS_CFG[selected.status].color}15`, color: STATUS_CFG[selected.status].color }}>
                  {(selected.display_name ?? "?")[0]?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-[#F2F2F2] text-sm font-semibold truncate">{selected.display_name}</p>
                  <p className="text-[#3D3D3D] text-[10px] truncate">{selected.email}</p>
                </div>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="w-7 h-7 flex items-center justify-center text-[#3D3D3D] hover:text-[#777777] cursor-pointer transition-colors">
                <i className="ri-close-line text-base"></i>
              </button>
            </div>

            {/* 문서 내용 */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div className="bg-[#0C0C0E] rounded-lg border border-white/[0.05] px-4 py-3">
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest mb-2">문서 정보</div>
                <div className="space-y-1.5">
                  {[["제목", selected.title], ["유형", DOC_LABELS[selected.doc_type] ?? selected.doc_type], ["제출일", new Date(selected.submitted_at).toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" })]].map(([k, v]) => (
                    <div key={k} className="flex items-start gap-3">
                      <span className="text-[#444444] text-[10px] w-10 shrink-0">{k}</span>
                      <span className="text-[#AAAAAA] text-xs">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 폼 데이터 */}
              {Object.keys(selected.form_data ?? {}).length > 0 && (
                <div className="bg-[#0C0C0E] rounded-lg border border-white/[0.05] px-4 py-3">
                  <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest mb-2">제출 내용</div>
                  <div className="space-y-2">
                    {Object.entries(selected.form_data).map(([k, v]) => (
                      <div key={k}>
                        <div className="text-[#444444] text-[9px] mb-0.5">{k}</div>
                        <p className="text-[#AAAAAA] text-xs leading-relaxed whitespace-pre-wrap">{String(v)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 기존 코멘트 */}
              {selected.admin_comment && selected.status !== "pending" && (
                <div className="rounded-lg border px-4 py-3" style={{ borderColor: STATUS_CFG[selected.status].border, backgroundColor: STATUS_CFG[selected.status].bg }}>
                  <div className="text-[10px] font-semibold mb-1" style={{ color: STATUS_CFG[selected.status].color }}>강사 코멘트</div>
                  <p className="text-[#888888] text-xs leading-relaxed">{selected.admin_comment}</p>
                </div>
              )}
            </div>

            {/* 액션 */}
            <div className="px-5 py-4 border-t border-white/[0.05] space-y-3 shrink-0">
              <div>
                <label className="text-[#555555] text-xs mb-1.5 flex items-center gap-1.5 block">
                  <i className="ri-chat-3-line text-sm"></i>강사 코멘트 <span className="text-[#3D3D3D]">(선택)</span>
                </label>
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder={selected.status !== "pending" ? "코멘트를 수정하려면 다시 처리하세요" : "승인 또는 반려 사유를 입력하세요. 학생 결재 문서에 표시됩니다."}
                  rows={3}
                  maxLength={400}
                  className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-xs rounded-lg px-3 py-2.5 border border-white/[0.08] focus:outline-none focus:border-[#FF9F43]/40 placeholder-[#2A2A2A] resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => handleAction("rejected")} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#E8375F]/30 bg-[#E8375F]/[0.08] text-[#E8375F] text-sm font-semibold hover:bg-[#E8375F]/15 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50">
                  <i className="ri-close-circle-line text-sm"></i>반려
                </button>
                <button type="button" onClick={() => handleAction("approved")} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#00C896] text-white text-sm font-semibold hover:bg-[#00DDA8] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50">
                  {saving ? <><i className="ri-loader-4-line animate-spin text-sm"></i>처리 중...</> : <><i className="ri-checkbox-circle-line text-sm"></i>승인</>}
                </button>
              </div>
              <p className="text-[#2A2A2A] text-[9px] text-center">처리 결과는 학생의 전자결재 페이지에 즉시 반영됩니다</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
